import { useMutation, useQueryClient } from '@tanstack/react-query';
import { countriesApi } from '../api/countries.api';
import type { CountryHierarchy } from '../types/countries.types';
import { COUNTRY_HIERARCHY_MULTI_SELECT_KEY, countryKeys } from './queryKeys';
import { regionKeys } from '@/features/regions/hooks/queryKeys';

/**
 * Ids to send for `root`'s subtree, ordered so no intermediate step leaves a child active under an
 * inactive parent — which the API rejects: enabling goes ancestors-first, disabling
 * descendants-first (a reversed preorder always puts descendants before their ancestor). `root` is
 * always included (it is the node the user toggled); descendants only when not already in the
 * target state.
 */
const collectCascadeIds = (root: CountryHierarchy, isActive: boolean): string[] => {
  const preorder = (item: CountryHierarchy, isRoot: boolean): string[] => [
    ...(isRoot || item.isActive !== isActive ? [item.id] : []),
    ...(item.children ?? []).flatMap((child) => preorder(child, false)),
  ];

  const ids = preorder(root, true);
  return isActive ? ids : ids.reverse();
};

interface ToggleStatusVariables {
  id: string;
  isActive: boolean;
  /**
   * `'enable'` cascades to descendants only when enabling; `'both'` cascades in either direction.
   * Omit for a single-node change.
   */
  cascade?: 'enable' | 'both';
  /** Subtree to cascade into. Loaded from the hierarchy endpoint when not supplied. */
  subtree?: CountryHierarchy;
}

export const useToggleCountryStatus = () => {
  const queryClient = useQueryClient();

  const toggleStatus = useMutation({
    mutationFn: async ({ id, isActive, cascade, subtree }: ToggleStatusVariables) => {
      const shouldCascade = cascade === 'both' || (cascade === 'enable' && isActive);
      const root = shouldCascade
        ? (subtree ?? (await countriesApi.getCountryHierarchy(id)).data)
        : undefined;
      const ids = root ? collectCascadeIds(root, isActive) : [id];

      for (const targetId of ids) {
        await countriesApi.toggleStatus(targetId, isActive);
      }
    },
    // onSettled, not onSuccess: a cascade can fail partway through, leaving real changes behind.
    onSettled: () => {
      // countryKeys.all, not lists(): a status change (and its cascade) also moves the detail,
      // hierarchy, territory and descendant queries.
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      queryClient.invalidateQueries({ queryKey: [COUNTRY_HIERARCHY_MULTI_SELECT_KEY] });
      queryClient.invalidateQueries({ queryKey: regionKeys.lists(), refetchType: 'all' });
    },
  });

  return {
    toggleStatus: toggleStatus.mutate,
    isToggling: toggleStatus.isPending,
  };
};
